/**
 * TaskComposer - "@ someone with a task".
 *
 * Typing @ in the box opens a people picker; choosing someone inserts their
 * name into the text AND sets them as the owner, so the mention and the
 * assignment can never disagree. Due date is required, same as every task.
 *
 * Doubles as the EDITOR: pass `initial` and it opens filled in. Editing reuses
 * this rather than getting its own form, so re-assigning a task works exactly
 * like assigning one - same @ picker, same rules.
 *
 * Optional Contact (T-02): pass `contacts` (the home site's people) and the
 * task can reference one of them. Never shown on the GLOBAL KSA tab, which has
 * no CRM contacts. A contact who has left the company is never offered (C-04),
 * but a task that already names one keeps them, labelled as having left.
 *
 * Tag people (10/6/26): beside the owner, a "Tag people" picker (TagPeople)
 * for the others involved. Picking a tagged person as the owner takes them
 * off the tagged list - an owner is never also tagged.
 */
import { useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useDataScope } from '../../context/DataScope';
import { isLeftCompany } from '../../data/crmStages';
import { contactHash, contactLabel } from '../crm/nextStep';
// the LOCAL day (the UTC day is a day ahead after about 8pm Eastern)
import { localDay as todayISO } from './tasksTable';
import TagPeople from './TagPeople';
import { taggedOf, tidyTagged } from './tagged';

/**
 * The optional Contact select shared by every task form. `current` is the
 * contact the task names today (from contactIndex): when it is not in the list
 * (they left the company, or moved to another site) it still shows by name,
 * so saving the form never drops it by accident.
 */
export function ContactSelect({ contacts, value, onChange, style, current }) {
  const list = contacts || [];
  const known = !value || list.some((c) => String(c.id) === String(value));
  const cur = current && value && String(current.id) === String(value) ? current : null;
  const offList = cur
    ? `${cur.name || 'Unnamed'} (${isLeftCompany(cur) ? 'left company' : 'another site'})`
    : '(contact from another site)';
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={style} title="Contact (optional)">
      <option value="">- No contact -</option>
      {list.map((c) => (
        <option key={c.id} value={c.id}>
          {contactLabel(c)}
        </option>
      ))}
      {/* a contact who has since left or moved off this site stays selectable */}
      {!known && <option value={value}>{offList}</option>}
    </select>
  );
}

const stopClick = (e) => e.stopPropagation();

/**
 * A task's contact in a list (T-02: "clicking the contact opens their
 * record"). The name opens the contact - `onOpen(contact)` when the screen can
 * show it in place, otherwise the #/crm?contact=<id> link (in a new browser
 * tab with `newTab`, so an open editor underneath is not lost) - and a small
 * mail icon beside it starts an email.
 */
export function ContactLink({ contact, onOpen, newTab = false }) {
  if (!contact) return null;
  const left = isLeftCompany(contact);
  const name = contact.name || 'Unnamed contact';
  const st = {
    color: left ? '#777' : '#1565C0',
    fontWeight: 700,
    textDecoration: 'none',
    cursor: 'pointer',
  };
  const title = `Open ${name}${left ? ' (left company)' : ''}${!onOpen && newTab ? ' in a new tab' : ''}`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
      {onOpen ? (
        <button
          type="button"
          title={title}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(contact);
          }}
          style={{ ...st, background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
        >
          {name}
        </button>
      ) : (
        <a
          href={contactHash(contact.id)}
          onClick={stopClick}
          style={st}
          title={title}
          {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          {name}
        </a>
      )}
      {left && <span style={{ fontSize: '0.85em', fontWeight: 600, color: '#888' }}>(left company)</span>}
      {contact.email && (
        <a
          href={`mailto:${contact.email}`}
          onClick={stopClick}
          title={`Email ${contact.email}`}
          aria-label={`Email ${name}`}
          style={{ display: 'inline-flex', color: '#888', textDecoration: 'none', lineHeight: 0 }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </a>
      )}
    </span>
  );
}

const input = {
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid #DDD',
  fontSize: 11.5,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
  color: '#0D0D0D',
  width: '100%',
  boxSizing: 'border-box',
};

export default function TaskComposer({ onSubmit, onCancel, autoFocus, compact, initial, submitLabel, contacts, currentContact }) {
  const { users } = useApp();
  const { user } = useAuth();
  const scope = useDataScope();
  const me = (user && user.name) || '';
  const [contactId, setContactId] = useState(initial && initial.contact_id ? String(initial.contact_id) : '');
  // shown only when there is someone to pick (or the task already names someone)
  const showContact = !scope && ((contacts && contacts.length > 0) || !!contactId);
  const [text, setText] = useState(initial ? initial.description || '' : '');
  const [owner, setOwnerRaw] = useState(initial ? initial.owner || me : me);
  const [tagged, setTagged] = useState(() => taggedOf(initial));
  // the new owner comes off the tagged list
  const setOwner = (name) => {
    setOwnerRaw(name);
    setTagged((cur) => tidyTagged(cur, name));
  };
  const [due, setDue] = useState(initial && initial.due_date ? String(initial.due_date).slice(0, 10) : todayISO());
  const [mention, setMention] = useState(null); // { query, at } while typing @…
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const people = useMemo(
    () =>
      (users || [])
        .map((u) => u.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [users]
  );
  const matches = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return people.filter((n) => n.toLowerCase().includes(q)).slice(0, 6);
  }, [mention, people]);

  const onText = (e) => {
    const v = e.target.value;
    setText(v);
    // an @ run with no space after it is an in-progress mention
    const upto = v.slice(0, e.target.selectionStart);
    const m = upto.match(/@([^\s@]*)$/);
    setMention(m ? { query: m[1], at: upto.length - m[0].length } : null);
  };

  const pick = (name) => {
    if (!mention) return;
    const before = text.slice(0, mention.at);
    const after = text.slice(mention.at + mention.query.length + 1);
    setText(`${before}@${name} ${after}`.replace(/\s+$/, ' '));
    setOwner(name); // the mention IS the assignment
    setMention(null);
    if (boxRef.current) boxRef.current.focus();
  };

  const submit = async () => {
    if (!text.trim() || !owner || !due || busy) return;
    setBusy(true);
    try {
      // contact_id only when the picker is on screen, so an edit elsewhere
      // never wipes a contact it could not show
      const vals = { description: text.trim(), owner, due_date: due, tagged: tidyTagged(tagged, owner) };
      if (showContact) vals.contact_id = contactId || null;
      const ok = await onSubmit(vals);
      // Adding clears the box for the next one; editing closes the row, so
      // wiping it here would blank the task for a frame on the way out.
      if (ok && !initial) {
        setText('');
        setDue(todayISO());
        setContactId('');
        setTagged([]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <textarea
          ref={boxRef}
          autoFocus={autoFocus}
          value={text}
          onChange={onText}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              if (mention) setMention(null);
              else if (onCancel) onCancel();
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          placeholder="What needs doing? Type @ to assign someone…"
          style={{ ...input, minHeight: compact ? 46 : 60, resize: 'vertical', lineHeight: 1.45 }}
        />
        {mention && matches.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 40,
              marginTop: 2,
              background: '#fff',
              border: '1px solid #DDD',
              borderRadius: 8,
              minWidth: 190,
              boxShadow: '0 8px 22px rgba(0,0,0,0.13)',
              overflow: 'hidden',
            }}
          >
            {matches.map((n) => (
              <button
                key={n}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(n);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '7px 11px',
                  border: 'none',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: 11.5,
                  fontFamily: 'Montserrat',
                  color: '#0D0D0D',
                }}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          style={{ ...input, width: 'auto', flex: '1 1 130px', cursor: 'pointer' }}
        >
          <option value="">- Assign to -</option>
          {Array.from(new Set([...(owner ? [owner] : []), ...people])).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <TagPeople value={tagged} onChange={setTagged} people={people} owner={owner} style={{ flex: '2 1 200px' }} />
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          style={{ ...input, width: 'auto', flex: '0 0 128px' }}
          title="Due date (required)"
        />
        {showContact && (
          <ContactSelect
            contacts={contacts}
            value={contactId}
            current={currentContact}
            onChange={setContactId}
            style={{ ...input, width: 'auto', flex: '1 1 150px', cursor: 'pointer' }}
          />
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || !owner || !due || busy}
          style={{
            padding: '7px 14px',
            borderRadius: 6,
            border: 'none',
            cursor: !text.trim() || !owner || busy ? 'not-allowed' : 'pointer',
            background: !text.trim() || !owner || busy ? '#C8D6C9' : '#1B5E20',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            fontFamily: 'Montserrat',
          }}
        >
          {busy ? (initial ? 'Saving…' : 'Adding…') : submitLabel || 'Add task'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '7px 12px',
              borderRadius: 6,
              border: '1px solid #DDD',
              background: '#fff',
              color: '#666',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
